<template>
  <div 
    class="element-item"
    :class="{ 
      'is-dragging': isDragging,
      'is-hover': isHovered && !disabled,
      'is-disabled': disabled
    }"
    :draggable="!disabled"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <div class="element-icon">
      <slot name="icon">
        <div v-if="iconSvg" v-html="iconSvg"></div>
      </slot>
    </div>
    <span class="element-label">{{ label }}</span>
    <div class="drag-handle">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 16C8.38333 16 7 15.45 5.85 14.35C4.7 13.25 4.08333 11.9 4 10.3L6.1 10.925C6.31667 11.825 6.78333 12.5625 7.5 13.1375C8.21667 13.7125 9.05 14 10 14C11.1 14 12.0417 13.6083 12.825 12.825C13.6083 12.0417 14 11.1 14 10C14 9.05 13.7125 8.21667 13.1375 7.5C12.5625 6.78333 11.825 6.31667 10.925 6.1L10.3 4C11.9 4.08333 13.25 4.7 14.35 5.85C15.45 7 16 8.38333 16 10C16 11.6667 15.4167 13.0833 14.25 14.25C13.0833 15.4167 11.6667 16 10 16ZM6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10V9.55C0 9.4 0.0166667 9.25 0.05 9.1L2 9.7V10C2 12.2333 2.775 14.125 4.325 15.675C5.875 17.225 7.76667 18 10 18C12.2333 18 14.125 17.225 15.675 15.675C17.225 14.125 18 12.2333 18 10C18 7.76667 17.225 5.875 15.675 4.325C14.125 2.775 12.2333 2 10 2H9.7L9.1 0.05C9.25 0.0166667 9.4 0 9.55 0H10C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20C8.61667 20 7.31667 19.7375 6.1 19.2125ZM8.525 10.5L4.25 6.225L3 10L0 0L10 3L6.225 4.25L10.5 8.525L8.525 10.5Z" fill="currentColor"/>
      </svg>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ElementItem',
  props: {
    type: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    iconSvg: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['drag-start', 'drag-end'],
  data() {
    return {
      isDragging: false,
      isHovered: false
    };
  },
  methods: {
    handleDragStart(event) {
      if (this.disabled) {
        event.preventDefault();
        return;
      }
      this.isDragging = true;
      const elementData = {
        type: this.type,
        label: this.label
      };
      event.dataTransfer.setData('application/json', JSON.stringify(elementData));
      event.dataTransfer.effectAllowed = 'copy';
      this.$emit('drag-start', elementData);
    },
    handleDragEnd() {
      this.isDragging = false;
      this.$emit('drag-end');
    }
  }
};
</script>

<style scoped>
.element-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(232, 244, 253, 0.5);
  border-radius: 5px;
  cursor: grab;
  transition: all 0.15s ease;
  width: 100%;
  box-sizing: border-box;
}

.element-item:hover,
.element-item.is-hover {
  background: #004879;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);
}

.element-item:hover .element-label,
.element-item.is-hover .element-label {
  color: #F9FBFD;
}

.element-item:hover .element-icon,
.element-item.is-hover .element-icon {
  color: #F9FBFD;
}

.element-item:hover .drag-handle,
.element-item.is-hover .drag-handle {
  color: #F9FBFD;
  opacity: 1;
  visibility: visible;
}

.element-item:active,
.element-item.is-dragging {
  cursor: grabbing;
  background: #004879;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.15);
  transform: scale(1.02);
}

.element-item.is-dragging .element-label {
  color: #F9FBFD;
}

.element-item.is-dragging .element-icon,
.element-item.is-dragging .drag-handle {
  color: #F9FBFD;
  opacity: 1;
  visibility: visible;
}

.element-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  color: #333;
  transition: color 0.15s ease;
}

.element-icon :deep(svg) {
  width: 18px;
  height: 18px;
}

.element-icon :deep(svg path) {
  fill: currentColor;
}

.drag-handle {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #333;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: color 0.15s ease, opacity 0.15s ease, visibility 0.15s ease;
}

.drag-handle svg {
  width: 20px;
  height: 20px;
}

.element-label {
  flex: 1;
  color: #333;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.element-item.is-disabled {
  background: #F2F2F2;
  cursor: not-allowed;
  pointer-events: none;
}
</style>
